/**
 * Standalone, embeddable Virtual Visit — app.brxs.ca/v/:visitId
 *
 *   ?unbranded=1  MLS-safe: no agent / brokerage / contact details, no Bricks mark
 *   ?embed=1      iframe mode: no page chrome, fills the frame
 *
 * Password-protected Visits show a gate; the unlock token lives in
 * sessionStorage for this tab only.
 */
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import VisitExperience from "@/components/visit/VisitExperience";
import VisitPasswordGate from "@/components/visit/VisitPasswordGate";
import { useVisitManifest } from "@/components/visit/useVisitManifest";
import { Wordmark } from "@/lib/bricks";

function truthy(v) {
  return ["1", "true", "yes"].includes(String(v || "").toLowerCase());
}

export default function VisitPage() {
  const { visitId } = useParams();
  const [params] = useSearchParams();
  const unbranded = truthy(params.get("unbranded")) || truthy(params.get("mls"));
  const embed = truthy(params.get("embed"));
  const { state, manifest, unlock } = useVisitManifest({ visitId, unbranded });

  useEffect(() => {
    const prev = document.title;
    const label = manifest?.address || manifest?.title || "Virtual Visit";
    document.title = unbranded ? `${label} · Virtual Visit` : `${label} · Virtual Visit · Bricks`;
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const prevRobots = meta.getAttribute("content");
    meta.setAttribute("content", "noindex");
    return () => {
      document.title = prev;
      if (created) meta.remove();
      else if (prevRobots != null) meta.setAttribute("content", prevRobots);
    };
  }, [manifest, unbranded]);

  const shell = { position: "fixed", inset: 0, background: "#0b0a09", color: "#F4F1EB", fontFamily: '"General Sans",ui-sans-serif,-apple-system,sans-serif' };
  const stage = { position: "absolute", left: 0, right: 0, top: 0, bottom: embed || unbranded ? 0 : 40 };

  let body = null;
  if (state === "locked") {
    body = <VisitPasswordGate fill onUnlock={unlock} title={manifest?.address ? `${manifest.address} · Private visit` : ""} />;
  } else if (state === "missing" || state === "error") {
    body = (
      <div data-testid="visit-missing" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, padding: 'var(--bx-pad)', textAlign: "center" }}>
        This Virtual Visit isn't available. The link may have been removed.
      </div>
    );
  } else if (state === "ready" && manifest) {
    body = <VisitExperience manifest={manifest} variant="page" embed={embed} showTitle />;
  }

  return (
    <div style={shell} data-testid="visit-page" data-unbranded={unbranded ? "1" : "0"}>
      <div style={stage}>{body}</div>
      {!embed && !unbranded && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 40, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", borderTop: "1px solid rgba(244,241,235,0.1)", fontSize: 12, color: "rgba(244,241,235,0.6)" }}>
          <span>Virtual Visit</span>
          <a href="https://www.brxs.ca" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <Wordmark size={14} color="#F4F1EB" />
          </a>
        </div>
      )}
    </div>
  );
}
